import { useEffect, useState, useRef, useMemo, useCallback, useReducer } from 'react'
import { getReferenceString, createReference, formatHumanName } from '@medplum/core'
import { Appointment, Encounter, Schedule } from '@medplum/fhirtypes'
import { useMedplum } from '@medplum/react'
import { useSubscription } from '@medplum/react-hooks'
import FullCalendar from '@fullcalendar/react'
import { DateClickArg } from '@fullcalendar/interaction'
import CalendarViewSwitcher from './components/CalendarViewSwitcher'
import TodayNav from './components/TodayNavigation'
import { Button, Group, Text, Tooltip, LoadingOverlay, Loader, Modal, Select } from '@mantine/core'
import AvailabilityModal from './components/AvailabilityModal/AvailabilityModal'
import { AppointmentModal } from './components/AppointmentModal'
import { RescheduleAppointmentModal } from './components/RescheduleAppointmentModal/RescheduleAppointmentModal'
import { CancelAppointmentModal } from './components/CancelAppointmentModal'
import { NoShowConfirmModal } from '@/pages/Appointment/components/AppointmentDetailsModal/NoShowConfirmModal'
import {
  notifyStatusChange,
  type StatusChangeResult,
} from '@/pages/Appointment/components/AppointmentDetailsModal/notifyStatusChange'
import {
  deleteFreeSlotsTodayUTC,
  createFreeSlotsForSchedule,
  createBlockedSlots,
  createSingleSlot,
  createAppointment,
  cancelAppointment,
} from './service'
import type { Draft } from './service'
import { showNotification } from '@mantine/notifications'
import { ViewType } from './types'
import { useNavigate } from 'react-router-dom'
import { IconClock } from '@tabler/icons-react'
import { AppointmentModalPayload } from '@/pages/Schedule/components/AppointmentModal/AppointmentModal.tsx'
import { useActivePractitioner } from '@/pages/Schedule/context/'
import { useCareCoordinatorRole } from '@/hooks/useCareCoordinatorRole'
import { useSessionState } from '@/hooks/useSessionState'
import { AppointmentSearch } from '@/pages/Schedule/components/AppointmentSearch'
import {
  fallsOutsideGridHours,
  type AppointmentSearchResult,
} from '@/pages/Schedule/components/appointment-search'
import { ScheduleCalendar } from '@/pages/Schedule/components/ScheduleCalendar'
import { ScheduleWeekView } from '@/pages/Schedule/components/ScheduleWeekView'
import { ScheduleMonthView } from '@/pages/Schedule/components/ScheduleMonthView'
import {
  filterVisibleEvents,
  getVisibleDays,
  isEventRendered,
  type AppointmentStatusValue,
  type CalendarEvent,
} from '@/pages/Schedule/components/scheduleCalendar.tokens'
import {
  ScheduleFilterSegments,
  type InsuranceFilter,
  type StatusFilter,
} from '@/pages/Schedule/components/ScheduleFilterSegments'
import { ListOfSelectedPractitioners } from '@/pages/Schedule/context/components'
import { DOCTOR_COLORS, PractitionerWithColor } from '@/pages/Schedule/context/components/types'
import { useReturningPatientMap, useMiddlewareClient } from '@/hooks'
import {
  useEncounterInsuranceVerdicts,
  useInvalidateInsuranceVerdicts,
} from '@/hooks/useInsuranceVerdicts'
import { logError } from '@/utils/errorUtils'
import { useClient } from '@/hooks/useClient'
import { AppointmentsCard } from '@/pages/ZocdocAdmin/AppointmentsCard'
import { viewDays } from '@/pages/ZocdocAdmin/dateRange'
import { ZOCDOC_APPOINTMENT_SYSTEM, type SelfPayStatus } from '@/model/middleware-types'
import { isSelfPayAppointment, readSelfPayStatus } from '@/utils/selfPay'

/** What one batched `Appointment` read tells the calendar about a visit. */
type AppointmentFacts = {
  status?: string
  zocdocAppointmentId?: string
  selfPayStatus?: SelfPayStatus
}

/**
 * appointmentId → the facts the calendar renders from the Appointment itself.
 *
 * The batch already fetched each Appointment **whole** to read its status, so the Zocdoc identifier
 * comes out of the same response rather than a second search — which is why this returns a record
 * of facts instead of growing a parallel map per field.
 */
async function fetchAppointmentFacts(
  medplum: ReturnType<typeof import('@medplum/react').useMedplum>,
  encounters: Encounter[],
): Promise<Record<string, AppointmentFacts>> {
  const appointmentIds = encounters
    .map((e) => e.appointment?.[0]?.reference?.split('/')[1])
    .filter((id): id is string => Boolean(id))

  if (appointmentIds.length === 0) return {}

  const bundle = await medplum.executeBatch({
    resourceType: 'Bundle',
    type: 'batch',
    entry: appointmentIds.map((id) => ({
      request: { method: 'GET', url: `Appointment/${id}` },
    })),
  })

  const factsById: Record<string, AppointmentFacts> = {}
  bundle.entry?.forEach((entry) => {
    const appt = entry.resource as Appointment | undefined
    if (appt?.resourceType !== 'Appointment' || !appt.id) return
    const zocdocAppointmentId = appt.identifier?.find(
      (identifier) => identifier.system === ZOCDOC_APPOINTMENT_SYSTEM,
    )?.value
    // Only a visit that still carries the self-pay marker owes the fee; a leftover extension on an
    // insured visit must not draw the badge.
    const selfPayStatus = isSelfPayAppointment(appt)
      ? (readSelfPayStatus(appt) ?? undefined)
      : undefined
    factsById[appt.id] = {
      ...(appt.status && { status: appt.status }),
      ...(zocdocAppointmentId && { zocdocAppointmentId }),
      ...(selfPayStatus && { selfPayStatus }),
    }
  })
  return factsById
}

/**
 * patientId → Patient.birthDate for the encounters in view.
 *
 * One `_elements`-narrowed search for every patient on screen, so the hover
 * preview (`<AppointmentPreview>`) can render DOB/age from the calendar event
 * instead of waiting on `useAppointmentInsurance`'s patient read.
 */
async function fetchPatientBirthDateMap(
  medplum: ReturnType<typeof import('@medplum/react').useMedplum>,
  encounters: Encounter[],
): Promise<Record<string, string>> {
  const patientIds = [
    ...new Set(
      encounters
        .map((e) => e.subject?.reference?.split('/')[1])
        .filter((id): id is string => Boolean(id)),
    ),
  ]

  if (patientIds.length === 0) return {}

  const patients = await medplum.searchResources('Patient', {
    _id: patientIds.join(','),
    _elements: 'birthDate',
    _count: patientIds.length,
  })

  const birthDateMap: Record<string, string> = {}
  for (const patient of patients) {
    if (patient.id && patient.birthDate) {
      birthDateMap[patient.id] = patient.birthDate
    }
  }
  return birthDateMap
}

export const SchedulePage = () => {
  const medplum = useMedplum()
  const client = useClient()
  const middleware = useMiddlewareClient()

  const navigate = useNavigate()
  const calendarRef = useRef<FullCalendar | null>(null)
  const {
    activePractitionerProfile: activePractitionerDataResource,
    isLoading: isLoadingPractitionerProfile,
    listOFPractitioners,
    selectActivePractitionerProfile,
    isStuffMember,
    selectedMultiplePractitionerIds: selectedPractitioners,
  } = useActivePractitioner()
  // Reassigning an appointment to another practitioner is a care-coordinator
  // action, so the popup only offers it to that role.
  const { isCareCoordinator } = useCareCoordinatorRole()
  // **Who may use the Zocdoc console here**, mirroring the middleware's `ZocdocOpsGuard`: the
  // coordinator role, and super admins, who hold no `PractitionerRole` for a role lookup to find
  // and so answer "no" to the coordinator check alone. It gates the Zocdoc table below the
  // calendar — the Integrations dashboard's own card, whose buttons cancel and confirm bookings
  // and create charts. A visit's own cancel and status change are every practitioner's, Zocdoc
  // half included, so they do not read this.
  const mayWriteToZocdoc = isCareCoordinator || medplum.isSuperAdmin()
  // View mode and the date the calendar is parked on are kept for the session,
  // so returning to Schedule shows the same week/month rather than today's week
  // (PD-1206). `anchorDate` is the view's own `currentStart` — FullCalendar's
  // reported range is padded in Month view, so it can't seed `initialDate`.
  const [activeView, setActiveView] = useSessionState<ViewType>(
    'schedule:activeView',
    'timeGridWeek',
  )
  const [anchorDate, setAnchorDate] = useSessionState<string | null>('schedule:anchorDate', null)
  const [schedule, setSchedule] = useState<Schedule | null>(null)
  const [events, setEvents] = useState<CalendarEvent[]>([]) // Define a more specific type if needed
  const [encounters, setEncounters] = useState<Encounter[]>([])
  const { data: insuranceVerdictMap, isPending: isInsuranceVerdictMapPending } =
    useEncounterInsuranceVerdicts(encounters)
  const invalidateInsuranceVerdicts = useInvalidateInsuranceVerdicts()
  const { data: returningPatientMap, isPending: isReturningPatientMapPending } =
    useReturningPatientMap(encounters)
  const [currentViewDates, setCurrentViewDates] = useState<{ start: Date; end: Date } | null>(null)
  // The Zocdoc table below the grid follows the week the grid is on, and re-reads after every
  // write made here — its beHuman column shows the same visits and slots this calendar does.
  const [zocdocRefreshKey, refreshZocdocTable] = useReducer((n: number) => n + 1, 0)
  const zocdocRange = useMemo(
    () => (currentViewDates ? viewDays(currentViewDates) : undefined),
    [currentViewDates],
  )
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(false)
  const [isSaveAvailabilityLoading, setIsSaveAvailabilityLoading] = useState(false)
  const [isSlotStatusUpdateModalOpen, setIsSlotStatusUpdateModalOpen] = useState(false)
  const [selectedSlotTime, setSelectedSlotTime] = useState<string | null>(null)
  const [isSendingMagicLink, setIsSendingMagicLink] = useState(false)
  const [practitionerOptions, setPractitionerOptions] = useState<PractitionerWithColor[]>([])
  const [isLoadingScheduleDetails, setIsLoadingScheduleDetails] = useState(true)
  const [reassignTarget, setReassignTarget] = useState<{
    appointmentId: string
    practitionerId?: string
  } | null>(null)
  const [selectedReassignPractitionerId, setSelectedReassignPractitionerId] = useState<
    string | null
  >(null)
  const [isReassigningAppointment, setIsReassigningAppointment] = useState(false)
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null)
  const loadSeqRef = useRef(0)
  const [loadedScope, setLoadedScope] = useState<{
    start: Date
    end: Date
    patientId: string | null
  } | null>(null)
  const [highlightedEncounterId, setHighlightedEncounterId] = useState<string | null>(null)
  const [pendingSearchPick, setPendingSearchPick] = useState<AppointmentSearchResult | null>(null)
  const [unreachablePick, setUnreachablePick] = useState<{
    result: AppointmentSearchResult
    reason: 'filters' | 'hours'
  } | null>(null)
  // Kept for the session so returning to Schedule keeps the filters the user
  // set instead of resetting them (PD-1206).
  const [statusFilters, setStatusFilters] = useSessionState<StatusFilter[]>(
    'schedule:statusFilters',
    [],
  )
  // Keyed apart from the old clinic-decision filter, whose stored values no longer exist.
  const [insuranceFilter, setInsuranceFilter] = useSessionState<InsuranceFilter>(
    'schedule:insuranceStatusFilter',
    'all',
  )
  // The third chip in the same filter row as Status and Insurance, cleared by the
  // same handler — so it is kept for the session like its row-mates, or the row
  // comes back with one chip lit and one dark (PD-1206).
  const [returningFilter, setReturningFilter] = useSessionState('schedule:returningFilter', false)
  const [rescheduleTarget, setRescheduleTarget] = useState<{
    encounterId: string
    practitionerId?: string
    patientId?: string
    currentStart?: string
    currentEnd?: string
    patientName?: string
  } | null>(null)
  // Set by the Cancel action; cleared by confirming or dismissing. `event` is
  // absent only if the encounter is no longer on the calendar.
  const [cancelTarget, setCancelTarget] = useState<{
    encounterId: string
    event?: CalendarEvent
  } | null>(null)
  const [isCancellingAppt, setIsCancellingAppt] = useState(false)

  const profile = useMemo(() => {
    if (!isLoadingPractitionerProfile && activePractitionerDataResource) {
      return activePractitionerDataResource
    }
  }, [isLoadingPractitionerProfile, activePractitionerDataResource])
  const practitionerSelectOptions = useMemo(
    () =>
      practitionerOptions.map((practitioner) => ({
        value: practitioner.id as string,
        label: formatHumanName(practitioner.name?.[0]) || practitioner.id || 'Unknown practitioner',
      })),
    [practitionerOptions],
  )
  const doctorNameById = useMemo(() => {
    const map: Record<string, string> = {}
    for (const practitioner of practitionerOptions) {
      if (practitioner.id) {
        map[practitioner.id] =
          formatHumanName(practitioner.name?.[0]) || practitioner.id || 'Unknown practitioner'
      }
    }
    return map
  }, [practitionerOptions])

  const closeReassignModal = useCallback(() => {
    setReassignTarget(null)
    setSelectedReassignPractitionerId(null)
  }, [])
  const loadEncountersForSelectedPractitioners = useCallback(async (): Promise<void> => {
    const seq = ++loadSeqRef.current
    setIsLoadingScheduleDetails(true)
    if (!currentViewDates || !medplum || selectedPractitioners.length === 0) {
      setEvents([])
      setIsLoadingScheduleDetails(false)
      return
    }
    try {
      medplum.invalidateSearches('Encounter')
      const { start, end } = currentViewDates
      const startDate = start.toISOString()
      const endDate = end.toISOString()
      let allEncounters: Encounter[] = []
      for (const practitionerId of selectedPractitioners) {
        const encounters = await medplum.searchResources(
          'Encounter',
          `practitioner=Practitioner/${practitionerId}&date=gt${startDate}&date=lt${endDate}&status:not=cancelled&class=http://terminology.hl7.org/CodeSystem/v3-ActCode|VR&_count=1000${selectedPatientId ? `&subject=Patient/${selectedPatientId}` : ''}`,
        )
        allEncounters = [...allEncounters, ...encounters]
      }
      if (allEncounters.length === 0) {
        if (seq !== loadSeqRef.current) return
        setEvents([])
        setLoadedScope({ start, end, patientId: selectedPatientId })
        setIsLoadingScheduleDetails(false)
        return
      }
      const [apptFacts, patientBirthDateMap] = await Promise.all([
        fetchAppointmentFacts(medplum, allEncounters),
        fetchPatientBirthDateMap(medplum, allEncounters),
      ])
      const calendarEvents = allEncounters.map((encounter) => {
        const patientName = encounter.subject?.display || 'Unknown Patient'
        const patientId = encounter.subject?.reference?.split('/')[1]
        const practitionerId = encounter.participant
          ?.find((p) => p.individual?.reference?.startsWith('Practitioner/'))
          ?.individual?.reference?.split('/')[1]
        const practionerOptionsItem = practitionerOptions.find((item) => item.id === practitionerId)
        const appointmentId = encounter.appointment?.[0]?.reference?.split('/')[1]

        return {
          id: encounter.id as string,
          type: 'appointment_slot',
          status: encounter.status,
          title: `${patientName}`,
          backgroundColor: practionerOptionsItem?.color || 'transparent',
          appointmentId,
          appointmentStatus: appointmentId ? apptFacts[appointmentId]?.status : undefined,
          zocdocAppointmentId: appointmentId
            ? apptFacts[appointmentId]?.zocdocAppointmentId
            : undefined,
          selfPayStatus: appointmentId ? apptFacts[appointmentId]?.selfPayStatus : undefined,
          practitionerId,
          patientId,
          patientBirthDate: patientId ? patientBirthDateMap[patientId] : undefined,
          reasonLabel: encounter.reasonCode?.[0]?.text,
          start: encounter.period?.start || '',
          end: encounter.period?.end || '',
        }
      })
      if (seq !== loadSeqRef.current) return
      setEncounters(allEncounters)
      setEvents(calendarEvents)
      setLoadedScope({ start, end, patientId: selectedPatientId })
    } catch (error) {
      logError('schedule', 'Load encounters for selected practitioners', error)
      showNotification({
        color: 'red',
        message: 'Error loading encounters for selected practitioners',
      })
    } finally {
      if (seq === loadSeqRef.current) setIsLoadingScheduleDetails(false)
    }
  }, [selectedPractitioners, currentViewDates, medplum, practitionerOptions, selectedPatientId])

  const loadEncounters = useCallback(async (): Promise<void> => {
    const seq = ++loadSeqRef.current
    setIsLoadingScheduleDetails(true)
    if (!currentViewDates || !profile || !medplum) {
      setIsLoadingScheduleDetails(false)
      return
    }
    try {
      medplum.invalidateSearches('Encounter')
      const { start, end } = currentViewDates
      // Format dates for FHIR search
      const startDate = start.toISOString()
      const endDate = end.toISOString()
      // Search for encounters within the date range
      const encounters = await medplum.searchResources(
        'Encounter',
        `practitioner=Practitioner/${profile.id}&date=gt${startDate}&date=lt${endDate}&status:not=cancelled&class=http://terminology.hl7.org/CodeSystem/v3-ActCode|VR&_count=1000${selectedPatientId ? `&subject=Patient/${selectedPatientId}` : ''}`,
      )

      const schedule = await medplum.searchOne('Schedule', { actor: getReferenceString(profile) })
      const availableSlots = schedule?.id
        ? await medplum.searchResources(
            'Slot',
            `schedule=Schedule/${schedule.id}&start=gt${startDate}&end=lt${endDate}&_count=1000`,
          )
        : []
      // Transform encounters to calendar events
      const filteredEncounters = encounters.filter((encounter) => encounter.status !== 'cancelled')
      const [apptFacts, patientBirthDateMap] = await Promise.all([
        fetchAppointmentFacts(medplum, filteredEncounters),
        fetchPatientBirthDateMap(medplum, filteredEncounters),
      ])
      const calendarEvents = filteredEncounters.map((encounter) => {
        const patientName = encounter.subject?.display || 'Unknown Patient'
        const patientId = encounter.subject?.reference?.split('/')[1]
        const practitionerId = encounter.participant
          ?.find((p) => p.individual?.reference?.startsWith('Practitioner/'))
          ?.individual?.reference?.split('/')[1]
        const practionerOptionsItem = practitionerOptions.find((item) => item.id === practitionerId)
        const encounterBackgroundColor =
          selectedPractitioners?.length > 1
            ? practionerOptionsItem?.color || 'transparent'
            : practionerOptionsItem?.color || DOCTOR_COLORS[0]
        const appointmentId = encounter.appointment?.[0]?.reference?.split('/')[1]
        return {
          id: encounter.id,
          type: 'appointment_slot',
          status: encounter.status,
          title: `${patientName}`,
          backgroundColor: encounterBackgroundColor,
          appointmentId,
          appointmentStatus: appointmentId ? apptFacts[appointmentId]?.status : undefined,
          zocdocAppointmentId: appointmentId
            ? apptFacts[appointmentId]?.zocdocAppointmentId
            : undefined,
          selfPayStatus: appointmentId ? apptFacts[appointmentId]?.selfPayStatus : undefined,
          practitionerId,
          patientId,
          patientBirthDate: patientId ? patientBirthDateMap[patientId] : undefined,
          reasonLabel: encounter.reasonCode?.[0]?.text,
          start: encounter.period?.start || '',
          end: encounter.period?.end || '',
        }
      })
      if (seq !== loadSeqRef.current) return
      setEncounters(filteredEncounters)

      const configuredSlots = availableSlots.map((slot) => {
        return {
          id: slot.id,
          title: 'Available',
          type: 'available_slot',
          start: slot.start || '',
          slotType: slot.status,
          end: slot.end || '',
          display: 'background',
          disabled: slot.status === 'busy-unavailable',
          backgroundColor:
            (slot.status === 'free' && 'rgba(52, 235, 125, 0.3)') ||
            (slot.status === 'busy-unavailable' && 'rgba(221, 161, 161, 100)') ||
            (slot.status === 'busy' && 'yellow') ||
            undefined,
        }
      })
      setEvents([...calendarEvents, ...configuredSlots])
      setLoadedScope({ start, end, patientId: selectedPatientId })
    } catch (error) {
      logError('schedule', 'Load encounters and slots', error)
      showNotification({
        color: 'red',
        message: 'Error loading encounters and slots',
      })
    } finally {
      if (seq === loadSeqRef.current) setIsLoadingScheduleDetails(false)
    }
  }, [
    medplum,
    profile,
    currentViewDates,
    selectedPatientId,
    practitionerOptions,
    selectedPractitioners?.length,
  ])
  const handleViewChange = (viewName: ViewType) => {
    const api = calendarRef.current?.getApi()
    if (api) {
      api.changeView(viewName)
      setActiveView(viewName)
    }
  }

  // Segment filters only hide encounter events; availability background bands
  // always stay visible. A status filter matches on the saved appointment
  // status, so encounters with no status saved are hidden by any specific
  // status filter and only reappear under "All".
  const visibleEvents = useMemo(
    () =>
      filterVisibleEvents(events, {
        statusFilters,
        insuranceFilter,
        returningFilter,
        insuranceVerdictMap,
        returningPatientMap,
      }),
    [
      events,
      statusFilters,
      insuranceFilter,
      returningFilter,
      insuranceVerdictMap,
      returningPatientMap,
    ],
  )

  // Events the active calendar view actually paints on the grid — the views clip
  // to their visible day columns (Mon–Fri) and, in Week/Day, to the 6am–7pm
  // window. The filter counts are derived from this same set so "All" matches
  // what's on screen exactly: not more, not less. Filters themselves are ignored
  // here (they're what the segments toggle), so the totals stay stable.
  const renderedEvents = useMemo(() => {
    if (!currentViewDates) return []
    const days = getVisibleDays(currentViewDates)
    const clipToDayHours = activeView !== 'dayGridMonth'
    return events.filter((event) => isEventRendered(event, days, clipToDayHours))
  }, [events, currentViewDates, activeView])

  const handleSetBlockSlots = async (
    blockPeriods: { startDate: Date; endDate: Date; startTime: string; endTime: string }[],
  ) => {
    if (!schedule || !schedule.id) {
      logError(
        'schedule',
        'Missing schedule',
        new Error('Schedule is null, cannot create free slots'),
      )
      return
    }
    try {
      setIsSaveAvailabilityLoading(true)
      await createBlockedSlots({
        blockedPeriods: blockPeriods,
        schedule,
        medplum,
      })
      refreshZocdocTable()
      await loadEncounters()
      showNotification({
        color: 'green',
        message: 'Availability was updated successfully',
      })
    } catch (error) {
      logError('schedule', 'Block slots', error)
      showNotification({
        color: 'red',
        message: 'Failed to update availability',
      })
    }
    setIsSaveAvailabilityLoading(false)
  }
  const handleSetAvailability = async (formData: { scheduleDraft: Draft; endsOn: Date | null }) => {
    if (!schedule || !schedule.id) {
      logError(
        'schedule',
        'Missing schedule',
        new Error('Schedule is null, cannot create free slots'),
      )
      return
    }
    try {
      setIsSaveAvailabilityLoading(true)
      const deleteResponse = await deleteFreeSlotsTodayUTC(medplum, schedule.id)
      if (deleteResponse.error) {
        logError('schedule', 'Delete free slots', deleteResponse.error)
        return
      } else {
        await createFreeSlotsForSchedule({
          medplum,
          schedule,
          formData,
        })
        // The Zocdoc sync hears about created slots but never about deleted ones, so a day the new
        // pattern left empty would stay bookable on Zocdoc. Name the days that lost slots; the
        // middleware ignores practitioners not on Zocdoc. Never blocks or fails the save.
        if (deleteResponse.deletedStarts.length > 0) {
          client
            .zocdocRepublishAvailability({
              scheduleId: schedule.id,
              instants: deleteResponse.deletedStarts,
            })
            .catch((error: unknown) =>
              logError('schedule', 'Zocdoc republish after set availability', error),
            )
        }
        refreshZocdocTable()
        await loadEncounters() // Reload encounters after setting availability

        showNotification({
          color: 'green',
          message: 'Availability set successfully',
        })
      }
      setIsAvailabilityModalOpen(false)
    } catch (error) {
      logError('schedule', 'Set availability', error)
      showNotification({
        color: 'red',
        message: 'Error setting availability',
      })
    } finally {
      setIsSaveAvailabilityLoading(false)
    }
  }
  const applyStatusChange = async (
    appointmentId: string,
    status: AppointmentStatusValue,
    pushToZocdoc = false,
  ) => {
    try {
      // The cast is the contract lagging: `pushToZocdoc` and the Zocdoc fields of the response are
      // on the route in `openapi.json`, but the published `@behuman.corp/contracts` has not been
      // regenerated, so its `paths` type does not carry them yet.
      const result = (await middleware.patch('/appointment/{id}/status', {
        id: appointmentId,
        status: status,
        ...(pushToZocdoc && { pushToZocdoc: true }),
      } as { id: string; status: AppointmentStatusValue })) as StatusChangeResult | undefined
      setEvents((prev) =>
        prev.map((e) =>
          e.appointmentId === appointmentId ? { ...e, appointmentStatus: status } : e,
        ),
      )
      notifyStatusChange(result)
      refreshZocdocTable()
      // Following the patient's Zocdoc reschedule moved the visit, so the grid is out of date.
      if (result?.zocdocMovedTo) reloadEncounters()
    } catch {
      showNotification({ color: 'red', message: 'Failed to update appointment status' })
    }
  }

  // A no-show on a Zocdoc-bound visit asks first, to offer recording it there too — the one
  // Zocdoc status that cannot be taken back. Every other status writes straight through, and a
  // bound visit's Zocdoc booking follows it automatically.
  const [noShowTarget, setNoShowTarget] = useState<CalendarEvent | null>(null)
  const handleStatusChange = async (appointmentId: string, status: AppointmentStatusValue) => {
    const event = events.find((e) => e.appointmentId === appointmentId)
    if (status === 'noshow' && event?.zocdocAppointmentId) {
      setNoShowTarget(event)
      return
    }
    await applyStatusChange(appointmentId, status)
  }

  const handleSendMagicLink = async (encounterId: string) => {
    try {
      setIsSendingMagicLink(true)
      await medplum.post('auth/magic-link/send-code', { encounterId })
      showNotification({
        color: 'green',
        message: 'Appointment link resent to patient',
      })
    } catch (error) {
      logError('schedule', 'Send magic link', error)
      showNotification({
        color: 'red',
        message: 'Failed to send magic link',
      })
    } finally {
      setIsSendingMagicLink(false)
    }
  }
  const handleCopyMagicLink = async (encounterId: string) => {
    let link = ''
    try {
      link = (await client.visitJoinLinkCreate({ encounterId })).link
    } catch (error) {
      logError('schedule', 'Copy appointment link', error)
      showNotification({
        color: 'red',
        message:
          error instanceof Error && error.message
            ? error.message
            : 'Could not copy the appointment link',
      })
      return
    }
    try {
      await navigator.clipboard.writeText(link)
      showNotification({ color: 'green', message: 'Appointment link copied to clipboard' })
    } catch {
      // Safari and Firefox refuse a clipboard write once the click's activation has lapsed.
      showNotification({
        color: 'yellow',
        autoClose: false,
        title: 'Copy this link',
        message: link,
      })
    }
  }
  const handleReassignAppointment = useCallback(async () => {
    if (!reassignTarget?.appointmentId || !selectedReassignPractitionerId) {
      return
    }

    try {
      setIsReassigningAppointment(true)
      await middleware.post('/appointment/reassign', {
        appointmentId: reassignTarget.appointmentId,
        practitionerId: selectedReassignPractitionerId,
      })
      showNotification({
        color: 'green',
        message: 'Appointment reassigned successfully',
      })
      closeReassignModal()
      refreshZocdocTable()

      if (selectedPractitioners.length > 1) {
        await loadEncountersForSelectedPractitioners()
      } else {
        await loadEncounters()
      }
    } catch (error) {
      logError('schedule', 'Reassign appointment', error)
      showNotification({
        color: 'red',
        message: (error as Error)?.message || 'Failed to reassign appointment',
      })
    } finally {
      setIsReassigningAppointment(false)
    }
  }, [
    closeReassignModal,
    loadEncounters,
    loadEncountersForSelectedPractitioners,
    middleware,
    reassignTarget,
    selectedPractitioners.length,
    selectedReassignPractitionerId,
  ])
  function handleSlotClick(calendarSlot: DateClickArg): void {
    // prevent interaction if no practitioner profile is selected in case of staff member user is logged in
    if ((!activePractitionerDataResource && isStuffMember) || selectedPractitioners.length > 1) {
      return
    }
    // if in month view, switch to day view on date click
    if (activeView === 'dayGridMonth') {
      calendarRef.current?.getApi().changeView('timeGridDay', calendarSlot.date)
      return
    }
    const { date } = calendarSlot
    setSelectedSlotTime(date.toISOString())
    if (date.toISOString() > new Date().toISOString()) {
      setIsSlotStatusUpdateModalOpen(true)
    }
  }
  // Custom Week grid uses this (it hands back a Date directly, not a FullCalendar arg).
  const canCreateSlot = !(
    (!activePractitionerDataResource && isStuffMember) ||
    selectedPractitioners.length > 1
  )
  const handleSlotSelect = (date: Date): void => {
    if (!canCreateSlot) return
    setSelectedSlotTime(date.toISOString())
    if (date.toISOString() > new Date().toISOString()) {
      setIsSlotStatusUpdateModalOpen(true)
    }
  }
  async function handleProcessSingleSlotAllocation(payload: AppointmentModalPayload) {
    if (!schedule || !schedule.id) {
      logError(
        'schedule',
        'Missing schedule',
        new Error('Schedule is null, cannot create free slots'),
      )
      return
    }
    const { start, end, status } = payload
    if (status !== 'appointment') {
      try {
        await createSingleSlot({ start, end, status, schedule, medplum })
        refreshZocdocTable()
        await loadEncounters()
      } catch (error) {
        logError('schedule', 'Create single slot', error)
      }
    } else {
      try {
        await createAppointment({
          ...payload,
          medplum,
          middleware,
          schedule,
        })
        refreshZocdocTable()
        await loadEncounters()
      } catch (error) {
        logError('schedule', 'Create appointment', error)
      }
    }
  }
  const loadScheduleForPractitioner = async (practitionerId: string) => {
    if (!medplum) {
      return
    }
    try {
      const practitioner = await medplum.readResource('Practitioner', practitionerId)
      if (!practitioner) {
        logError('schedule', 'Practitioner not found', new Error('Practitioner not found'))
        return
      }
      const foundSchedule = await medplum.searchOne('Schedule', {
        actor: getReferenceString(practitioner),
      })
      if (foundSchedule) {
        setSchedule(foundSchedule)
        return foundSchedule
      } else {
        const newSchedule = await medplum.createResource({
          resourceType: 'Schedule',
          actor: [createReference(practitioner)],
          active: true,
        })
        setSchedule(newSchedule)
        return newSchedule
      }
    } catch (error) {
      logError('schedule', 'Load or create schedule', error)
    }
  }
  const subscriptionPractitionerIds =
    selectedPractitioners.length > 0 ? selectedPractitioners : profile?.id ? [profile.id] : []
  // Watch the resource the calendar actually renders. This used to listen on
  // `Communication?recipient=…`, which reloaded the whole calendar every time
  // the practitioner received a message and never reloaded it when an
  // appointment changed.
  const subscriptionCriteria =
    subscriptionPractitionerIds.length > 0
      ? `Encounter?practitioner=${subscriptionPractitionerIds.map((id) => `Practitioner/${id}`).join(',')}`
      : undefined

  const refreshEncounters = useCallback(() => {
    if (selectedPractitioners.length > 1) {
      void loadEncountersForSelectedPractitioners()
    } else {
      void loadEncounters()
    }
  }, [selectedPractitioners.length, loadEncountersForSelectedPractitioners, loadEncounters])

  useSubscription(subscriptionCriteria, refreshEncounters)

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshEncounters()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [refreshEncounters])

  useEffect(() => {
    if (listOFPractitioners.length > 0) {
      const options = listOFPractitioners.map((practitioner, index) => ({
        ...practitioner,
        color: DOCTOR_COLORS[index % DOCTOR_COLORS.length],
      }))
      setPractitionerOptions(options)
    }
  }, [listOFPractitioners, selectedPractitioners])

  useEffect(() => {
    if (!medplum || !profile || !profile.id) {
      return
    }
    setIsLoadingScheduleDetails(true)
    loadScheduleForPractitioner(profile.id)
      .then(() => loadEncounters())
      .catch(() => setIsLoadingScheduleDetails(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    medplum,
    profile,
    activePractitionerDataResource,
    currentViewDates,
    activeView,
    selectedPatientId,
  ])

  useEffect(() => {
    // If multiple practitioners are selected,
    //  we load all encounters for those practitioners without showing available slots,
    //  because they can be different for each practitioner and it can be confusing to show them together on the calendar.
    //  User can select single practitioner to see available slots and set availability.
    if (selectedPractitioners.length > 1 && practitionerOptions.length > 0) {
      loadEncountersForSelectedPractitioners()
      selectActivePractitionerProfile(null)
    } else if (selectedPractitioners.length === 1) {
      selectActivePractitionerProfile(selectedPractitioners[0])
    } else {
      selectActivePractitionerProfile(null)
      setEvents([])
    }
  }, [
    selectedPractitioners,
    currentViewDates,
    listOFPractitioners,
    practitionerOptions,
    selectActivePractitionerProfile,
    loadEncountersForSelectedPractitioners,
  ])

  const isAvailabilityLoading = isSaveAvailabilityLoading || isLoadingPractitionerProfile
  const isAvailabilityDisabled =
    !profile ||
    !activePractitionerDataResource ||
    selectedPractitioners.length > 1 ||
    isLoadingPractitionerProfile

  // Card-level actions, wired into ScheduleCalendar.
  const handleRescheduleAppt = (appt: CalendarEvent) => {
    const enc = encounters.find((en) => en.id === appt.id)
    const patientId = enc?.subject?.reference?.split('/')[1]
    setRescheduleTarget({
      encounterId: appt.id,
      practitionerId: appt.practitionerId,
      patientId,
      currentStart: appt.start,
      currentEnd: appt.end,
      patientName: appt.title,
    })
  }
  const handleReassignAppt = (appt: CalendarEvent) => {
    setReassignTarget({
      appointmentId: appt.appointmentId as string,
      practitionerId: appt.practitionerId,
    })
    setSelectedReassignPractitionerId(null)
  }
  // A transferred appointment leaves this calendar, so pull the encounters again
  // (same branch handleCancelAppt uses).
  const reloadEncounters = () => {
    if (selectedPractitioners.length > 1) {
      void loadEncountersForSelectedPractitioners()
    } else {
      void loadEncounters()
    }
  }

  // Cancelling notifies the patient and is not undoable from here, so the click
  // only opens a confirmation — `confirmCancelAppt` is what actually cancels.
  // The event is looked up by id so the confirmation can restate the
  // appointment, keeping the callback signature the calendar views already pass.
  const handleCancelAppt = (encounterId: string) => {
    setCancelTarget({
      encounterId,
      event: events.find((event) => event.id === encounterId),
    })
  }

  const confirmCancelAppt = ({ cancelZocdoc }: { cancelZocdoc: boolean }) => {
    const encounterId = cancelTarget?.encounterId
    if (!encounterId) {
      return
    }

    setIsCancellingAppt(true)
    // `cancelAppointment` reports its own failures and resolves false, so there
    // is nothing to catch here.
    void cancelAppointment({ middleware, id: encounterId, cancelZocdoc })
      .then((success) => {
        setCancelTarget(null)
        if (!success) return
        refreshZocdocTable()
        reloadEncounters()
      })
      .finally(() => setIsCancellingAppt(false))
  }

  const searchPractitionerIds = useMemo(
    () => (isStuffMember ? selectedPractitioners : profile?.id ? [profile.id] : []),
    [isStuffMember, selectedPractitioners, profile?.id],
  )

  const handleSearchPick = (result: AppointmentSearchResult) => {
    setUnreachablePick(null)
    setHighlightedEncounterId(null)
    setSelectedPatientId(result.patientId ?? null)
    calendarRef.current?.getApi().changeView('timeGridDay', new Date(result.start))
    if (fallsOutsideGridHours(result.start)) {
      setUnreachablePick({ result, reason: 'hours' })
      return
    }
    setPendingSearchPick(result)
  }

  const handleSearchClear = () => {
    setSelectedPatientId(null)
    setHighlightedEncounterId(null)
    setPendingSearchPick(null)
    setUnreachablePick(null)
  }

  const resetSegmentFilters = () => {
    const pick = unreachablePick?.result
    setStatusFilters([])
    setInsuranceFilter('all')
    setReturningFilter(false)
    setUnreachablePick(null)
    if (pick) setPendingSearchPick(pick)
  }

  useEffect(() => {
    if (!pendingSearchPick || isLoadingScheduleDetails || !loadedScope) return
    if (loadedScope.patientId !== (pendingSearchPick.patientId ?? null)) return
    const startMs = new Date(pendingSearchPick.start).getTime()
    if (startMs < loadedScope.start.getTime() || startMs >= loadedScope.end.getTime()) return

    const { encounterId } = pendingSearchPick
    if (!events.some((event) => event.id === encounterId)) {
      setPendingSearchPick(null)
      return
    }
    if (insuranceFilter !== 'all' && !insuranceVerdictMap) return
    if (returningFilter && !returningPatientMap) return

    setPendingSearchPick(null)
    if (!visibleEvents.some((event) => event.id === encounterId)) {
      setUnreachablePick({ result: pendingSearchPick, reason: 'filters' })
      return
    }
    setHighlightedEncounterId(encounterId)
    requestAnimationFrame(() => {
      document
        .querySelector(`[data-appointment-id="${encounterId}"]`)
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    })
  }, [
    pendingSearchPick,
    isLoadingScheduleDetails,
    loadedScope,
    events,
    visibleEvents,
    insuranceFilter,
    returningFilter,
    insuranceVerdictMap,
    returningPatientMap,
  ])

  useEffect(() => {
    if (!highlightedEncounterId) return
    const timer = setTimeout(() => setHighlightedEncounterId(null), 6000)
    return () => clearTimeout(timer)
  }, [highlightedEncounterId])

  // `?encounter=<id>&start=<iso>` from the daily briefing: ids only, never a patient name
  // (URLs reach Sentry breadcrumbs). Only the mount-time value counts.
  const linkParams = new URLSearchParams(window.location.search)
  const linkedPick = useRef<{ encounterId: string; start: string } | null>(
    linkParams.get('encounter') && linkParams.get('start')
      ? { encounterId: linkParams.get('encounter')!, start: linkParams.get('start')! }
      : null,
  )
  const [autoOpenPreviewId, setAutoOpenPreviewId] = useState<string | null>(null)
  useEffect(() => {
    const pick = linkedPick.current
    if (!pick || !currentViewDates || Number.isNaN(Date.parse(pick.start))) return
    linkedPick.current = null
    handleSearchPick({ ...pick, end: pick.start, patientName: '' })
    // The grid never renders an off-hours visit, so its preview cannot open.
    if (!fallsOutsideGridHours(pick.start)) setAutoOpenPreviewId(pick.encounterId)
  }, [currentViewDates])

  const unreachableSubject = unreachablePick?.result.patientName
    ? `${unreachablePick.result.patientName}'s appointment`
    : 'This appointment'

  return (
    <div className="schedule-v2-scope min-h-[calc(100vh-54px)] bg-[#F6F7F9] p-4 text-[#1F2733]">
      <div className="rounded-[18px] border border-[#E7E9EC] bg-white px-[26px] pt-6 pb-7 shadow-[0_1px_3px_rgba(16,24,40,.05),0_12px_30px_rgba(16,24,40,.05)]">
        <Modal
          opened={!!reassignTarget}
          onClose={closeReassignModal}
          title="Reassign appointment"
          centered
        >
          <div className="flex flex-col gap-4">
            <Select
              label="Practitioner"
              placeholder="Select practitioner"
              data={practitionerSelectOptions.filter(
                (practitioner) => practitioner.value !== reassignTarget?.practitionerId,
              )}
              value={selectedReassignPractitionerId}
              onChange={setSelectedReassignPractitionerId}
              searchable
              nothingFoundMessage="No practitioners found"
            />
            <Button
              onClick={handleReassignAppointment}
              loading={isReassigningAppointment}
              disabled={
                !selectedReassignPractitionerId ||
                selectedReassignPractitionerId === reassignTarget?.practitionerId
              }
            >
              Submit
            </Button>
          </div>
        </Modal>
        <Modal
          opened={!!unreachablePick}
          onClose={() => setUnreachablePick(null)}
          title={
            unreachablePick?.reason === 'filters'
              ? 'Appointment hidden by filters'
              : 'Appointment outside calendar hours'
          }
          centered
        >
          <div className="flex flex-col gap-4">
            <Text size="sm">
              {unreachablePick?.reason === 'filters' ? (
                <>
                  {unreachableSubject} is on this day, but the active status, insurance or
                  returning-patient filters hide it from the calendar.
                </>
              ) : (
                <>
                  {unreachableSubject} starts outside the hours the Week and Day grids show. Switch
                  to Month view to see it.
                </>
              )}
            </Text>
            <div className="flex justify-end gap-2">
              <Button variant="default" onClick={() => setUnreachablePick(null)}>
                {unreachablePick?.reason === 'filters' ? 'Keep filters' : 'Close'}
              </Button>
              {unreachablePick?.reason === 'filters' && (
                <Button onClick={resetSegmentFilters}>Reset filters</Button>
              )}
            </div>
          </div>
        </Modal>
        <div className="sticky top-16 z-10 bg-white pt-1 pb-2">
          {/* Header row — title + calendar navigation (SCHEDULE_V2_DESIGN.md) */}
          <div className="flex flex-wrap items-center gap-4">
            <h1 className="m-0 text-[28px] font-extrabold tracking-[-0.6px]">Schedule</h1>

            {/* Patient search + set availability (doctor selection lives in the
                doctor-selection panel below) */}
            <div className="flex flex-wrap items-center gap-3">
              <AppointmentSearch
                practitionerIds={searchPractitionerIds}
                doctorNameById={doctorNameById}
                referenceMs={currentViewDates?.start.getTime()}
                onPick={handleSearchPick}
                onClear={handleSearchClear}
              />
              <Tooltip
                label="Select single practitioner to be able to set availability and see free slots on the calendar"
                disabled={selectedPractitioners.length <= 1}
                multiline
                w={288}
                withArrow
                position="bottom-start"
                styles={{
                  tooltip: {
                    background: '#2B2F36',
                    fontSize: 12.5,
                    fontWeight: 500,
                    lineHeight: 1.5,
                    padding: '11px 13px',
                    borderRadius: 8,
                  },
                }}
              >
                <span className="inline-flex">
                  <button
                    type="button"
                    onClick={() => setIsAvailabilityModalOpen(true)}
                    disabled={isAvailabilityDisabled}
                    className="schedule-v2-mulish inline-flex shrink-0 items-center gap-1.5 rounded-md border-none px-2.5 py-[3px] !text-[16px] !font-bold text-white transition enabled:cursor-pointer enabled:bg-[#E5533D] enabled:shadow-[0_1px_2px_rgba(203,71,31,.3)] enabled:hover:bg-[#D4472F] disabled:cursor-not-allowed disabled:border disabled:border-[#ECEEF1] disabled:bg-[#F4F5F7] disabled:text-[#B4B9C0]"
                  >
                    {isAvailabilityLoading ? (
                      <Loader size={15} color={isAvailabilityDisabled ? '#B4B9C0' : '#fff'} />
                    ) : (
                      <IconClock size={15} stroke={2} />
                    )}
                    Set availability
                  </button>
                </span>
              </Tooltip>
              {!isStuffMember && (
                <Group gap="lg" className="text-sm text-gray-500">
                  <Group gap={4}>
                    <span className="h-2 w-2 rounded-full border border-green-300 bg-transparent"></span>
                    <Text>Available</Text>
                  </Group>
                  <Group gap={4}>
                    <span className="h-2 w-2 rounded-full bg-blue-500"></span>
                    <Text>Appointment</Text>
                  </Group>
                </Group>
              )}
            </div>

            {/* Calendar navigation — pushed to the far right */}
            <div className="ml-auto flex items-center gap-3">
              <TodayNav
                onPrev={() => calendarRef.current?.getApi().prev()}
                onToday={() => calendarRef.current?.getApi().today()}
                onNext={() => calendarRef.current?.getApi().next()}
                activeView={activeView}
                title={calendarRef.current?.getApi ? calendarRef.current.getApi().view.title : ''}
              />
              <span className="h-6 w-px bg-[#E4E7EB]" />
              <CalendarViewSwitcher activeView={activeView} onViewChange={handleViewChange} />
            </div>
          </div>
        </div>

        {/* Doctor selection — always visible for staff, independent of the
            filter panel toggle. */}
        {isStuffMember && <ListOfSelectedPractitioners practitionerOptions={practitionerOptions} />}

        {/* Filter panel — status/insurance filters, always visible. */}
        <ScheduleFilterSegments
          events={renderedEvents}
          insuranceVerdictMap={insuranceVerdictMap}
          returningPatientMap={returningPatientMap}
          insuranceFilterPending={isInsuranceVerdictMapPending && insuranceVerdictMap === undefined}
          returningFilterPending={isReturningPatientMapPending && returningPatientMap === undefined}
          statusFilters={statusFilters}
          insuranceFilter={insuranceFilter}
          returningFilter={returningFilter}
          onStatusFiltersChange={setStatusFilters}
          onInsuranceFilterChange={setInsuranceFilter}
          onReturningFilterChange={setReturningFilter}
        />

        <div className="relative">
          <LoadingOverlay visible={isLoadingScheduleDetails} />
          {/* FullCalendar is kept mounted but always hidden — it only owns date
              navigation (prev/next/today/changeView) and reports the visible
              range. Week and Day render the custom ScheduleWeekView; Month the
              custom ScheduleMonthView. */}
          <div className="hidden">
            <ScheduleCalendar
              calendarRef={calendarRef}
              events={visibleEvents}
              activeView={activeView}
              initialDate={anchorDate ?? undefined}
              isStuffMember={isStuffMember}
              selectedPractitionerCount={selectedPractitioners.length}
              isSendingMagicLink={isSendingMagicLink}
              insuranceVerdictMap={insuranceVerdictMap}
              returningPatientMap={returningPatientMap}
              onOpenCall={(id) => navigate(`/call/${id}`)}
              onReschedule={handleRescheduleAppt}
              onReassign={handleReassignAppt}
              onCancel={handleCancelAppt}
              onStatusChange={handleStatusChange}
              onSendMagicLink={handleSendMagicLink}
              onDatesSet={(arg) => {
                setActiveView(arg.view.type as ViewType)
                setAnchorDate(arg.view.currentStart.toISOString())
                setCurrentViewDates({ start: arg.start, end: arg.end })
              }}
              onSlotClick={handleSlotClick}
            />
          </div>
          {(activeView === 'timeGridWeek' || activeView === 'timeGridDay') && currentViewDates && (
            <ScheduleWeekView
              range={currentViewDates}
              events={visibleEvents}
              isStuffMember={isStuffMember}
              isCareCoordinator={isCareCoordinator}
              selectedPractitionerCount={selectedPractitioners.length}
              isSendingMagicLink={isSendingMagicLink}
              insuranceVerdictMap={insuranceVerdictMap}
              returningPatientMap={returningPatientMap}
              doctorNameById={doctorNameById}
              onOpenCall={(id) => navigate(`/call/${id}`)}
              onReschedule={handleRescheduleAppt}
              onReassign={handleReassignAppt}
              onCancel={handleCancelAppt}
              isCancelConfirmOpen={!!cancelTarget || !!noShowTarget}
              onStatusChange={handleStatusChange}
              onSendMagicLink={handleSendMagicLink}
              onCopyMagicLink={handleCopyMagicLink}
              onClinicDecisionChange={invalidateInsuranceVerdicts}
              onAppointmentTransferred={reloadEncounters}
              onJumpToAppointment={handleSearchPick}
              highlightedEventId={highlightedEncounterId}
              autoOpenPreviewId={autoOpenPreviewId}
              onAutoOpenPreviewHandled={() => setAutoOpenPreviewId(null)}
              onSlotSelect={handleSlotSelect}
              canCreateSlot={canCreateSlot}
            />
          )}
          {activeView === 'dayGridMonth' && currentViewDates && (
            <ScheduleMonthView
              range={currentViewDates}
              events={visibleEvents}
              isStuffMember={isStuffMember}
              isCareCoordinator={isCareCoordinator}
              selectedPractitionerCount={selectedPractitioners.length}
              isSendingMagicLink={isSendingMagicLink}
              insuranceVerdictMap={insuranceVerdictMap}
              returningPatientMap={returningPatientMap}
              doctorNameById={doctorNameById}
              onOpenCall={(id) => navigate(`/call/${id}`)}
              onReschedule={handleRescheduleAppt}
              onReassign={handleReassignAppt}
              onCancel={handleCancelAppt}
              isCancelConfirmOpen={!!cancelTarget || !!noShowTarget}
              onStatusChange={handleStatusChange}
              onSendMagicLink={handleSendMagicLink}
              onCopyMagicLink={handleCopyMagicLink}
              onClinicDecisionChange={invalidateInsuranceVerdicts}
              onAppointmentTransferred={reloadEncounters}
              onJumpToAppointment={handleSearchPick}
              highlightedEventId={highlightedEncounterId}
              onSelectDay={(date) => calendarRef.current?.getApi().changeView('timeGridDay', date)}
            />
          )}
        </div>

        {selectedSlotTime && (
          <AppointmentModal
            opened={isSlotStatusUpdateModalOpen}
            onClose={() => setIsSlotStatusUpdateModalOpen(false)}
            selectedSlotTime={selectedSlotTime}
            processTimeAllocation={handleProcessSingleSlotAllocation}
          />
        )}
        <AvailabilityModal
          opened={isAvailabilityModalOpen}
          onClose={() => setIsAvailabilityModalOpen(false)}
          setAvailabilitySlots={handleSetAvailability}
          setBlockedSlots={handleSetBlockSlots}
        />
        <CancelAppointmentModal
          opened={!!cancelTarget}
          appointment={cancelTarget?.event}
          cancelling={isCancellingAppt}
          onClose={() => setCancelTarget(null)}
          onConfirm={confirmCancelAppt}
        />
        <NoShowConfirmModal
          opened={!!noShowTarget}
          zocdocAppointmentId={noShowTarget?.zocdocAppointmentId}
          startAt={noShowTarget?.start}
          onClose={() => setNoShowTarget(null)}
          onConfirm={({ pushToZocdoc }) => {
            const appointmentId = noShowTarget?.appointmentId
            setNoShowTarget(null)
            if (appointmentId) void applyStatusChange(appointmentId, 'noshow', pushToZocdoc)
          }}
        />
        {rescheduleTarget && (
          <RescheduleAppointmentModal
            opened={!!rescheduleTarget}
            onClose={() => setRescheduleTarget(null)}
            onSuccess={() => {
              refreshZocdocTable()
              if (selectedPractitioners.length > 1) {
                loadEncountersForSelectedPractitioners()
              } else {
                loadEncounters()
              }
            }}
            encounterId={rescheduleTarget.encounterId}
            practitionerId={rescheduleTarget.practitionerId}
            patientId={rescheduleTarget.patientId}
            orgPractitionerIds={
              rescheduleTarget.practitionerId ? [rescheduleTarget.practitionerId] : []
            }
            currentStart={rescheduleTarget.currentStart}
            currentEnd={rescheduleTarget.currentEnd}
            patientName={rescheduleTarget.patientName}
          />
        )}
      </div>

      {/* Zocdoc's own bookings, read straight from Zocdoc — the same card the Integrations
          dashboard shows. Its own panel on the page ground rather than inside Schedule's card,
          because it is a second surface and not part of the calendar. Its From/To window follows
          the week the grid is on; the pickers still narrow it until the week changes. Week view
          only. */}
      {activeView === 'timeGridWeek' && mayWriteToZocdoc && (
        <div className="behuman-v2-scope mt-4">
          <AppointmentsCard
            title="Zocdoc appointments"
            range={zocdocRange}
            refreshKey={zocdocRefreshKey}
            onChanged={refreshEncounters}
          />
        </div>
      )}
    </div>
  )
}
